



































































































































import Vue from "vue";
import { Component } from "vue-property-decorator";
import CbxExpenseType from "@/views/lookups/CbxExpenseType.vue";
import CbxPaymentMethod from "@/views/lookups/CbxPaymentMethod.vue";
import CbxPaymentFrequency from "@/views/lookups/CbxPaymentFrequency.vue";
import {
  FactFindExpenseDto,
  CreateExpenseCommand,
  OwnerDto,
  ExpenseTypeDto,
  PaymentMethodDto,
  PaymentFrequencyDto,
  UpdateExpenseCommand,
  ClientSelector
} from "@/api/ClientPortalApiClient";
import ApiFactory from "../../api/ApiFactory";
import CbxOwner from "@/views/lookups/CbxOwner.vue";
import AlertService from "../../services/alert.service";
import { LoadingSpinnerService } from "@/store";

@Component({
  components: {
    CbxOwner,
    CbxExpenseType,
    CbxPaymentMethod,
    CbxPaymentFrequency
  }
})
export default class FactFindExpenses extends Vue {
  private alertService = new AlertService();
  private contactId: number = 0;
  private expenses: FactFindExpenseDto[] = [];
  private emptyExpense = new FactFindExpenseDto();
  private selectExpense = new FactFindExpenseDto();
  private displayModel = new FactFindExpenseDto();
  private formEnabled: boolean = false;
  private mode: string = "default";
  private clientNames: OwnerDto[] = [];
  private modelSig = "";

  $refs!: {
    owner: CbxOwner;
    expenseType: CbxExpenseType;
    paymentMethod: CbxPaymentMethod;
    paymentFrequency: CbxPaymentFrequency;
    mainForm: HTMLFormElement;
  };

  async created() {
    this.contactId = parseInt(this.$route.params.contactId);
    this.getExpenses(true);
    await this.getClientSelectorNames();
  }

  private async getClientSelectorNames() {
    const api = ApiFactory.CreateDataClient();
    const data = await api.owner_Index(this.contactId);
    this.clientNames = data || [];
  }

  private expenseDisplayName(expense: FactFindExpenseDto): string {
    const owner = this.clientNames.find(x => x.id === expense.client);
    return (
      (owner ? owner.name : "") +
      " - " +
      (expense.expenseType ? expense.expenseType.name : "")
    );
  }

  private saveExpense(e: Event) {
    e.preventDefault();
    if (this.mode === "update") {
      this.update();
    } else if (this.mode === "create") {
      this.createNew();
    }
  }

  private async createNew(): Promise<boolean> {
    LoadingSpinnerService.Show("Saving new Expense...");
    const api = ApiFactory.CreateDataClient();
    let cmd = new CreateExpenseCommand();

    cmd.contactId = this.contactId;
    cmd.client = this.displayModel.client;
    cmd.expenseAmount = this.displayModel.expenseAmount;
    cmd.monthlyExpenseAmount = this.displayModel.monthlyExpenseAmount;

    if (this.displayModel.expenseType) {
      cmd.expenseTypeId = this.displayModel.expenseType.id;
    }
    if (this.displayModel.paymentMethod) {
      cmd.paymentMethodId = this.displayModel.paymentMethod.id;
    }
    if (this.displayModel.paymentFrequency) {
      cmd.paymentFrequencyId = this.displayModel.paymentFrequency.id;
    }

    try {
      const result = await api.factFindExpense_IndexPOST(cmd);
      this.displayModel.id = result;
      await this.getExpenses();
      this.displayModel =
        this.expenses.find(x => x.id === result) || this.displayModel;
      this.mode = "update";
      this.modelSig = this.displayModel.toJSON();
      LoadingSpinnerService.Success("Expense saved successfully");
      return true;
    } catch (e) {
      LoadingSpinnerService.Failure(
        "Could not save Expense. Please try again."
      );
    }

    return false;
  }

  public async update(): Promise<boolean> {
    LoadingSpinnerService.Show("Updating Expense...");
    const api = ApiFactory.CreateDataClient();
    let cmd = new UpdateExpenseCommand();

    cmd.id = this.displayModel.id;
    cmd.client = this.displayModel.client;
    cmd.expenseAmount = this.displayModel.expenseAmount;
    cmd.monthlyExpenseAmount = this.displayModel.monthlyExpenseAmount;

    if (this.displayModel.expenseType) {
      cmd.expenseTypeId = this.displayModel.expenseType.id;
    }
    if (this.displayModel.paymentMethod) {
      cmd.paymentMethodId = this.displayModel.paymentMethod.id;
    }
    if (this.displayModel.paymentFrequency) {
      cmd.paymentFrequencyId = this.displayModel.paymentFrequency.id;
    }

    try {
      await api.factFindExpense_IndexPUT(cmd);
      this.modelSig = JSON.stringify(this.displayModel.toJSON()).toString();
      LoadingSpinnerService.Success("Expense updated successfully");
      return true;
    } catch (e) {
      LoadingSpinnerService.Failure(
        "Could not save Expense. Please try again."
      );
    }

    return false;
  }

  private async getExpenses(showLoader: boolean | undefined = undefined) {
    if (showLoader) LoadingSpinnerService.Show("Loading Expenses...");
    const api = ApiFactory.CreateDataClient();
    try {
      const result = await api.factFindExpense_IndexAll(this.contactId);
      this.expenses = result || [];
      if (showLoader) LoadingSpinnerService.Hide();
    } catch (e) {
      this.expenses = [];
      LoadingSpinnerService.Failure(
        "Could not load Expenses. Please try again."
      );
    }
  }

  private async rowSelected(
    item: FactFindExpenseDto,
    index: number,
    event: Event
  ) {
    if (!(await this.checkForChanges())) {
      return;
    }

    this.formEnabled = true;
    this.mode = "update";
    this.displayModel = item;
    this.modelSig = JSON.stringify(this.displayModel.toJSON()).toString();

    if (this.displayModel.client) {
      this.$refs.owner.setSelected(this.displayModel.client);
    } else {
      this.$refs.owner.setSelected(ClientSelector.Main);
    }

    this.$refs.expenseType.setSelected(this.displayModel.expenseType);
    this.$refs.paymentMethod.setSelected(this.displayModel.paymentMethod);
    this.$refs.paymentFrequency.setSelected(this.displayModel.paymentFrequency);
  }

  private saveClicked() {
    this.formEnabled = false;
    this.clearData();
    this.mode = "default";
  }

  private createClicked() {
    this.clearData();
    this.mode = "create";
    this.formEnabled = true;
  }

  private updateClicked() {
    this.mode = "update";
    this.formEnabled = true;
  }

  private clearData() {
    this.displayModel = this.emptyExpense;
    this.displayModel.client = ClientSelector.Main;
    this.$refs.owner.setSelected(ClientSelector.Main);
    this.$refs.expenseType.setSelected(new ExpenseTypeDto());
    this.$refs.paymentMethod.setSelected(new PaymentMethodDto());
    this.$refs.paymentFrequency.setSelected(new PaymentFrequencyDto());
  }

  public async checkForChanges(): Promise<boolean> {
    if (this.isDisplayModelEmpty() == false) {
      if (this.hasChanges() == true) {
        if (confirm("Would you like to save your changes?")) {
          return await this.saveChanges();
        }
      }
    }

    return true;
  }

  public async saveChanges(): Promise<boolean> {
    if (this.$refs.mainForm.checkValidity()) {
      if (this.mode === "update") {
        return await this.update();
      } else if (this.mode === "create") {
        return await this.createNew();
      }

      return true;
    }

    this.alertService.error(
      "Form Validation Failed",
      "Please complete all required fields"
    );
    return false;
  }

  private isDisplayModelEmpty(): boolean {
    return (
      this.mode !== "create" &&
      JSON.stringify(this.displayModel.toJSON()) ===
        JSON.stringify(this.emptyExpense.toJSON())
    );
  }

  private hasChanges(): boolean {
    const s1 = JSON.stringify(this.displayModel.toJSON()).toString();
    if (s1 !== this.modelSig) {
      return true;
    } else {
      return false;
    }
  }

  private ownerSelected(owner: ClientSelector) {
    this.displayModel.client = owner;
  }
  private expenseTypeSelected(expenseType: ExpenseTypeDto) {
    this.displayModel.expenseType = expenseType;
  }
  private paymentMethondSelected(paymentMethod: PaymentMethodDto) {
    this.displayModel.paymentMethod = paymentMethod;
  }
  private paymentFrequencySelected(paymentFrequency: PaymentFrequencyDto) {
    this.displayModel.paymentFrequency = paymentFrequency;
  }
}
